import React from "react";
import {
  Button,
  Iconography,
  IconographySizes,
  Notification,
  NotificationVariants,
} from "@hid-galaxy-ui/galaxy-react";
import { useAppDispatch, useAppSelector } from "../../../../../../redux";
import {
  getLookupByCategorySagaAction,
  lookupMappingsSagaAction,
} from "../../../../../../redux/saga/lookup-data/saga-actions";
import {
  LookupDataItemType,
  selectLookupCategoryData,
  selectLookupStatus,
} from "../../../../../../redux/slices/lookup-data";
import "./PartnerSetting.scss";

const TOAST_AUTO_CLOSE_MS = 4500;
const MODULE_NAME = "partner_program";

const parseModules = (metadata: unknown): string[] => {
  if (!metadata) return [];
  if (typeof metadata === "string") {
    try {
      const parsed = JSON.parse(metadata);
      return Array.isArray(parsed?.modules) ? parsed.modules : [];
    } catch {
      return [];
    }
  }
  if (typeof metadata === "object" && metadata !== null) {
    const modules = (metadata as { modules?: unknown }).modules;
    return Array.isArray(modules) ? modules.map((item) => String(item)) : [];
  }
  return [];
};

const PartnerSettingTab: React.FC<{ readOnly?: boolean }> = ({
  readOnly = false,
}) => {
  const dispatch = useAppDispatch();
  const partnerTypes = useAppSelector((state) =>
    selectLookupCategoryData(state, "partnerType")
  );
  const lookupStatus = useAppSelector(selectLookupStatus);
  const [selectedIds, setSelectedIds] = React.useState<number[]>([]);
  const [toasts, setToasts] = React.useState<any[]>([]);

  React.useEffect(() => {
    dispatch(getLookupByCategorySagaAction({ category: "partnerType" }));
  }, [dispatch]);

  React.useEffect(() => {
    const preselected = (partnerTypes as LookupDataItemType[])
      .filter((item) => parseModules(item.metadata).includes(MODULE_NAME))
      .map((item) => Number(item.ID))
      .filter((item) => !Number.isNaN(item));
    setSelectedIds(preselected);
  }, [partnerTypes]);

  const pushSuccessToast = (message: string) => {
    setToasts((prev) => [
      ...prev,
      {
        id: Date.now(),
        title: "",
        message,
        variant: NotificationVariants.Success,
        icon: (
          <Iconography icon="circleCheck" size={IconographySizes.Medium} />
        ),
      },
    ]);
  };

  const pushErrorToast = (message: string) => {
    setToasts((prev) => [
      ...prev,
      {
        id: Date.now(),
        title: "",
        message,
        variant: NotificationVariants.Error,
        icon: <Iconography icon="circleExclamation" size={IconographySizes.Medium} />,
      },
    ]);
  };

  const toggleChip = (id: number, checked: boolean) => {
    setSelectedIds((prev) => {
      if (checked) {
        return prev.includes(id) ? prev : [...prev, id];
      }
      return prev.filter((item) => item !== id);
    });
  };

  const onSave = () => {
    dispatch(
      lookupMappingsSagaAction({
        category: "partnerType",
        module: MODULE_NAME,
        ids: selectedIds,
        onSuccess: (message) => {
          pushSuccessToast(message || "Partner type mappings saved successfully.");
          dispatch(getLookupByCategorySagaAction({ category: "partnerType" }));
        },
        onError: (message) => {
          pushErrorToast(message || "Unable to save partner type mappings.");
        },
      })
    );
  };

  return (
    <div className="partner-setting-tab">
      <div className="roles-permissions-notifications-host">
        <Notification
          toasts={toasts}
          setToasts={setToasts}
          autoClose={TOAST_AUTO_CLOSE_MS}
          isAutoClose={true}
        />
      </div>

      <section className="ptv-container">
        <header className="ptv-container-header">
          <h3>Partner Type</h3>
          <p>Configure partner type</p>
        </header>

        <div className="ptv-block">
          <header className="ptv-block-header">
            <h4>Partner Types</h4>
            <Button
              variant="primary"
              label="SAVE"
              onClick={onSave}
              disabled={readOnly || lookupStatus.mappingsLoading}
            />
          </header>

          {lookupStatus.listLoading.partnerType ? (
            <div className="ptv-empty-list">Loading partner types...</div>
          ) : (
            <div className="ptv-chip-grid">
              {(partnerTypes as LookupDataItemType[]).map((item) => {
                const isChecked = selectedIds.includes(Number(item.ID));
                return (
                  <label
                    key={item.ID}
                    className={`ptv-chip ptv-chip-checkbox ${
                      isChecked ? "is-checked ptv-chip-label-checked" : ""
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      disabled={readOnly}
                      onChange={(event) =>
                        toggleChip(Number(item.ID), event.target.checked)
                      }
                    />
                    <span className="ptv-chip-text">{item.value}</span>
                  </label>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default PartnerSettingTab;
